import { useState, useEffect } from 'react';
import { Card, Row, Col, Button, Typography, List, Tag, Spin, App, Alert } from 'antd';
import { CheckOutlined, CrownOutlined, StarOutlined, RocketOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { useGetPlansQuery, useGetSubscriptionStatusQuery } from '../services/stripeApi';
import { useGetUserLimitInfoQuery } from '../services/usersApi';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../hooks/redux';
import { updateSubscriptionInfo } from '../store/slices/authSlice';
import EmbeddedCheckoutModal from '../components/stripe/EmbeddedCheckoutModal';
import StripeTrustBadge from '../components/common/StripeTrustBadge';
import { ONBOARDING_PAGE_BACKGROUND } from '../styles/onboardingSurface';
import { clearPlanIntent, readPlanIntent } from '../utils/planIntent';
import { isAdmin, isSupportUser } from '../utils/roleHelpers';
import { seatNotice } from '../utils/seatNotice';

const { Title, Text } = Typography;

const planIcons: Record<string, React.ReactNode> = {
  Basico: <StarOutlined style={{ fontSize: 32 }} />,
  Estandar: <CrownOutlined style={{ fontSize: 32 }} />,
  Profesional: <RocketOutlined style={{ fontSize: 32 }} />,
};

const SelectPlan = () => {
  const { message } = App.useApp();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const { data: plans, isLoading } = useGetPlansQuery();
  const { data: subscriptionStatus, refetch: refetchSubscription } = useGetSubscriptionStatusQuery();
  const { data: limitInfo, refetch: refetchLimitInfo } = useGetUserLimitInfoQuery();
  const [selectedPlan, setSelectedPlan] = useState<{ name: string; displayName: string } | null>(null);
  const [showCheckoutModal, setShowCheckoutModal] = useState(false);
  const [urlPlanProcessed, setUrlPlanProcessed] = useState(false);

  const { trialEndDate, user } = useAppSelector(state => state.auth);
  // Contratar es del administrador: el backend rechaza el cobro a cualquier otro rol.
  const canSubscribe = isAdmin(user?.role) || isSupportUser(user?.email);

  // Check if this is a returning customer (no trial available)
  const isReturningCustomer = subscriptionStatus?.hasHadSubscription ?? false;
  // Check if internal trial is still valid
  const isTrialStillValid = trialEndDate && new Date(trialEndDate) > new Date();

  // Check for plan parameter in URL (coming from registration with selected plan)
  const planFromUrl = searchParams.get('plan');

  // El plan que eligió en la landing al registrarse. Solo se destaca: abrir el cobro
  // solo días después sería intrusivo (el ?plan= de la URL sí lo abre, como antes).
  const [intendedPlan] = useState(() => readPlanIntent());

  // Un aviso por tarjeta, calculado una vez por render.
  const seatNotices = new Map<string, string | null>(
    (plans ?? []).map((p) => [
      p.name,
      limitInfo ? seatNotice(p, limitInfo.currentUsers, limitInfo.grantedMaxUsers ?? 1, plans ?? []) : null,
    ]),
  );

  // Auto-open checkout modal if plan is specified in URL
  useEffect(() => {
    if (planFromUrl && plans && !urlPlanProcessed && canSubscribe) {
      // Find the plan in available plans
      const plan = plans.find(p => p.name.toLowerCase() === planFromUrl.toLowerCase());
      if (plan) {
        setSelectedPlan({ name: plan.name, displayName: plan.displayName });
        setShowCheckoutModal(true);
        setUrlPlanProcessed(true);
        // Clear the URL parameter after a small delay to avoid race conditions
        setTimeout(() => {
          setSearchParams({}, { replace: true });
        }, 100);
      }
    }
  }, [planFromUrl, plans, urlPlanProcessed, setSearchParams, canSubscribe]);

  const handleSelectPlan = (planName: string, displayName: string) => {
    setSelectedPlan({ name: planName, displayName });
    setShowCheckoutModal(true);
  };

  const handleCheckoutComplete = async () => {
    message.success('Suscripcion completada exitosamente');
    clearPlanIntent();
    // Refetch subscription data to update Redux state
    try {
      const [subStatus, limitData] = await Promise.all([
        refetchSubscription().unwrap(),
        refetchLimitInfo().unwrap(),
      ]);

      if (subStatus && limitData) {
        const isActive = subStatus.status === 'active' || subStatus.status === 'trialing';
        dispatch(updateSubscriptionInfo({
          plan: subStatus.planName || limitData.plan,
          isTrialExpired: !isActive && limitData.isTrialExpired,
          maxUsers: limitData.maxUsers,
          trialEndDate: subStatus.trialEnd || limitData.trialEndDate,
        }));
      }
    } catch (error) {
      console.error('Error refreshing subscription data:', error);
    }
    navigate('/subscription/success');
  };

  const handleCheckoutClose = () => {
    setShowCheckoutModal(false);
    setSelectedPlan(null);
  };

  const handleSkip = () => {
    // User wants to continue with trial
    navigate('/');
  };

  if (isLoading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        background: ONBOARDING_PAGE_BACKGROUND
      }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      padding: '40px 20px',
      background: ONBOARDING_PAGE_BACKGROUND
    }}>
      {/* Embedded Checkout Modal */}
      <EmbeddedCheckoutModal
        open={showCheckoutModal}
        planName={selectedPlan?.name || ''}
        planDisplayName={selectedPlan?.displayName}
        onClose={handleCheckoutClose}
        onComplete={handleCheckoutComplete}
      />

      <div style={{ maxWidth: 1200, margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: 48 }}>
          <Title level={2}>Selecciona tu Plan</Title>
          <Text type="secondary" style={{ fontSize: 16 }}>
            Elige el plan que mejor se adapte a las necesidades de tu negocio
          </Text>
          {!canSubscribe && (
            <Alert
              style={{ maxWidth: 600, margin: '16px auto 0' }}
              message="Solo el administrador puede contratar un plan"
              description="Pídele al administrador de tu restaurante que active la suscripción."
              type="info"
              showIcon
            />
          )}
          {isReturningCustomer && (
            <Alert
              style={{ maxWidth: 600, margin: '16px auto 0' }}
              message="Bienvenido de vuelta"
              description="Como ya has tenido una suscripción anteriormente, el cobro se realizará inmediatamente al contratar un nuevo plan."
              type="info"
              showIcon
              icon={<InfoCircleOutlined />}
            />
          )}
        </div>

        <Row gutter={[24, 24]} justify="center">
          {plans?.map((plan, index) => (
            <Col xs={24} sm={12} lg={8} key={plan.name}>
              <Card
                hoverable
                style={{
                  height: '100%',
                  borderRadius: 16,
                  border: plan.name === 'Estandar' ? '3px solid var(--color-primary)' : '1px solid var(--border-color)',
                  transform: plan.name === 'Estandar' ? 'scale(1.02)' : 'none',
                  boxShadow: plan.name === 'Estandar'
                    ? '0 8px 24px rgba(var(--color-primary-rgb), 0.2)'
                    : '0 4px 12px var(--shadow-color)',
                }}
              >
                {plan.name === 'Estandar' && (
                  <Tag
                    color="var(--color-primary)"
                    style={{
                      position: 'absolute',
                      top: -12,
                      left: '50%',
                      transform: 'translateX(-50%)',
                      padding: '4px 16px',
                      fontSize: 12,
                      fontWeight: 600,
                    }}
                  >
                    Más Popular
                  </Tag>
                )}

                <div style={{ textAlign: 'center', marginBottom: 24 }}>
                  <div style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 64,
                    height: 64,
                    borderRadius: 16,
                    background: plan.name === 'Estandar' ? 'var(--color-primary)' : 'var(--bg-tertiary)',
                    color: plan.name === 'Estandar' ? 'white' : 'var(--color-primary)',
                    marginBottom: 16,
                  }}>
                    {planIcons[plan.name]}
                  </div>

                  <Title level={3} style={{ marginBottom: 8 }}>
                    {plan.displayName}
                  </Title>

                  <div>
                    <Text style={{ fontSize: 40, fontWeight: 800, color: 'var(--color-primary)' }}>
                      ${plan.price}
                    </Text>
                    <Text type="secondary">/mes</Text>
                  </div>

                  {intendedPlan === plan.name && (
                    <Tag color="blue" style={{ marginTop: 8, marginInlineEnd: 0 }}>
                      El que elegiste al registrarte
                    </Tag>
                  )}
                </div>

                {seatNotices.get(plan.name) && (
                  <Alert
                    type="warning"
                    showIcon
                    style={{ marginBottom: 16 }}
                    message={seatNotices.get(plan.name)}
                  />
                )}

                <List
                  dataSource={plan.features}
                  renderItem={(feature) => (
                    <List.Item style={{ padding: '8px 0', border: 'none' }}>
                      <CheckOutlined style={{ color: 'var(--color-primary)', marginRight: 12 }} />
                      <Text>{feature}</Text>
                    </List.Item>
                  )}
                  style={{ marginBottom: 24 }}
                />

                <Button
                  type={plan.name === 'Estandar' ? 'primary' : 'default'}
                  size="large"
                  block
                  disabled={!canSubscribe}
                  onClick={() => handleSelectPlan(plan.name, plan.displayName)}
                  style={{
                    height: 48,
                    borderRadius: 8,
                    fontWeight: 600,
                    background: plan.name === 'Estandar' ? 'var(--color-primary)' : undefined,
                    borderColor: plan.name === 'Estandar' ? 'var(--color-primary)' : 'var(--color-primary)',
                    color: plan.name === 'Estandar' ? 'white' : 'var(--color-primary)',
                  }}
                >
                  Seleccionar Plan
                </Button>
              </Card>
            </Col>
          ))}
        </Row>

        <div style={{ textAlign: 'center', marginTop: 48 }}>
          {isTrialStillValid && (
            <>
              <Button type="link" onClick={handleSkip} style={{ color: 'var(--text-secondary)' }}>
                Continuar con el periodo de prueba gratuito
              </Button>
              <br />
            </>
          )}
          <Text type="secondary" style={{ fontSize: 12 }}>
            Puedes cambiar de plan en cualquier momento desde la configuración
          </Text>
        </div>

        {/* Stripe Security Notice */}
        <StripeTrustBadge style={{ maxWidth: 600, margin: '48px auto 0' }} />
      </div>
    </div>
  );
};

export default SelectPlan;
